import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { fetchScheduleDates } from '../services/eventTimelineRepository';
import { previewSchedulerConfig } from '../utils/normalizeScheduler';
import { useSmartBetaStrategyStore } from '@/features/Strategies/stores/definitionFormStore';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';

/**
 * The schedule's dates from POST /v1/scheduler/dates, shared by both hooks below.
 *
 * Uses the SAME queryKey + normalized payload as `useEventTimeline`, so the
 * preview's "as of" badge, step 1's point-in-time queries and the step-2 timeline
 * all read ONE fetch and never refetch each other's data.
 */
function useScheduleDatesQuery() {
  const schedulerInput = useSmartBetaStrategyStore(
    useShallow((s) => ({
      start_date: s.rebalance_scheduler.start_date,
      end_date: s.rebalance_scheduler.end_date,
      recon_scheduler: s.rebalance_scheduler.recon_scheduler,
      is_rebal_activated: s.rebalance_scheduler.is_rebal_activated,
      rebal_scheduler: s.rebalance_scheduler.rebal_scheduler,
    })),
  );

  // Same normalized payload as useEventTimeline so the queryKey matches and the
  // cache is shared (toggling an irrelevant greyed-out field won't refetch).
  //
  // `scheduler` stays null until the store actually holds a recon scheduler.
  // Normalizing reads `recon_scheduler.frequency` unguarded, so a store without
  // one throws — which used to be unreachable, because only step 2 called this.
  // Step 1 now calls it too (to resolve the universe at the reconstitution
  // date), and step 1 can be reached before the schedule exists at all. An
  // absent schedule is a legitimate "no date yet", not an error.
  const requestPayload = useMemo(
    () =>
      schedulerInput.recon_scheduler
        ? {
            start_date: schedulerInput.start_date,
            end_date: schedulerInput.end_date,
            scheduler: previewSchedulerConfig({
              recon_scheduler: schedulerInput.recon_scheduler,
              is_rebal_activated: schedulerInput.is_rebal_activated,
              rebal_scheduler: schedulerInput.rebal_scheduler,
            }),
          }
        : null,
    [schedulerInput],
  );

  const { data } = useQuery({
    queryKey: ['timeline-dates', requestPayload],
    queryFn: () => fetchScheduleDates(requestPayload!),
    staleTime: 5_000,
    gcTime: 60_000,
    enabled:
      !!requestPayload &&
      !!schedulerInput.start_date &&
      !!schedulerInput.end_date,
    placeholderData: (prev) => prev,
  });

  const latestRecon = useMemo(() => {
    const events = data?.reconstitutionEvents ?? [];
    if (events.length === 0) return null;
    // recon dates are 'YYYY-MM-DD' → lexical max is chronological max.
    return events.reduce(
      (max, e) => (e.date > max ? e.date : max),
      events[0].date,
    );
  }, [data]);

  return { latestRecon, reconAsOfByDate: data?.reconAsOfByDate };
}

/**
 * The latest reconstitution date — `max(recon_dates)`. This is when the resulting
 * portfolio becomes EFFECTIVE, so it's the right thing to display ("Portfolio as
 * of …"). For querying the universe, use `useUniverseAsOfDate` instead.
 *
 * Returns null while the dates load, if the backtest window is unset, or if the
 * schedule produced no reconstitution (so the caller can fall back to N/A).
 */
export function useLatestReconstitutionDate(): string | null {
  const { latestRecon } = useScheduleDatesQuery();
  // Debounce the RESOLVED date, not the schedule payload: the heavy point-in-
  // time queries downstream (universe catalogue + security-items, keyed on this
  // date) then refetch once the user stops editing the schedule, not on every
  // intermediate change. The /scheduler/dates query stays keyed on the raw
  // payload, so it keeps sharing its cache with useEventTimeline.
  return useDebouncedValue(latestRecon, 400);
}

/**
 * The DATA-AS-OF date for the latest reconstitution — its lagged reference date
 * (`ref_date` / `custom_lag_days`), NOT the event date.
 *
 * This is the snapshot the engine actually reads to select constituents at that
 * reconstitution, so it's the date every point-in-time universe query in step 1
 * must use. Querying the reconstitution date instead made step 1 count a
 * snapshot the backtest never looks at — which is exactly why its Constituents
 * figure disagreed with step 2's event table (e.g. 490 as of the 15th vs the 491
 * of the prior month-end the engine actually uses).
 *
 * Falls back to the reconstitution date when the API predates
 * `recon_asof_dates`, so an older backend degrades to the previous behaviour
 * rather than breaking.
 */
export function useUniverseAsOfDate(): string | null {
  const { latestRecon, reconAsOfByDate } = useScheduleDatesQuery();
  const asOf = useMemo(() => {
    if (!latestRecon) return null;
    return reconAsOfByDate?.[latestRecon] ?? latestRecon;
  }, [latestRecon, reconAsOfByDate]);
  return useDebouncedValue(asOf, 400);
}
